import GlobalOutlined from "@ant-design/icons/GlobalOutlined";
import { ChevronRight, Globe } from "lucide-react";
import React from "react";
import RowLabel from "common/Home/Sider/RowLabel.tsx";
import { getNewLookTokens } from "common/NewLook/tokens.ts";
import LookIcon from "common/Icons/LookIcon.tsx";
import styled from "styled-components";
import { SelectValue } from "antd/es/select";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import Select from "common/Select/Select.tsx";
import Flex from "common/Boxes/Flex.tsx";
import Text from "common/Typography/Text.tsx";
import { LanguageNameSpaces } from "@shared/types";
import {
  DEFAULT_LANGUAGE,
  LANGUAGE_OPTIONS,
  setExplicitLanguageSelection,
} from "utils/language.ts";

interface LanguageSelectorProps {
  /** New look menu: a full-width 48px row, icon left, value and chevron right. */
  row?: boolean;
}

const LanguageSelector = ({ row }: LanguageSelectorProps) => {
  const { i18n, t } = useTypedTranslation(LanguageNameSpaces.COMMON);

  const changeLanguage = (language: SelectValue) => {
    if (typeof language === "string") {
      setExplicitLanguageSelection();
      i18n.changeLanguage(language);
    }
  };

  if (row) {
    return (
      <RowWrapper>
        <RowLabel icon={<Globe size={20} strokeWidth={1.75} />}>
          {t("language_selector.title")}
        </RowLabel>
        <RowSelect
          onChange={changeLanguage}
          value={i18n.language}
          defaultValue={DEFAULT_LANGUAGE}
          optionLabelProp="label"
          options={LANGUAGE_OPTIONS}
          variant="borderless"
          mobileTitle={t("language_selector.mobile_title")}
          popupMatchSelectWidth={false}
          showSearch={false}
          suffixIcon={<ChevronRight size={16} strokeWidth={1.75} />}
          aria-label={t("language_selector.title")}
        />
      </RowWrapper>
    );
  }

  return (
    <Flex style={{ width: "100%" }}>
      <Text>{t("language_selector.title")}</Text>
      <StyledSelect
        onChange={changeLanguage}
        value={i18n.language}
        defaultValue={DEFAULT_LANGUAGE}
        prefix={
          <LookIcon classic={<GlobalIcon />} newLook={<NewLookGlobalIcon />} />
        }
        optionLabelProp="label"
        options={LANGUAGE_OPTIONS}
        variant="borderless"
        mobileTitle={t("language_selector.mobile_title")}
        popupMatchSelectWidth={false}
        showSearch={false}
      />
    </Flex>
  );
};

export default LanguageSelector;

const NewLookGlobalIcon = styled(Globe).attrs({ size: 16, strokeWidth: 1.75 })`
  padding-right: 2px;
  color: ${(p) => p.theme.colors.secondaryGrey} !important;
`;
const GlobalIcon = styled(GlobalOutlined)`
  font-size: 16px;
  padding-right: 2px;
  color: ${(p) => p.theme.colors.secondaryGrey} !important;
`;
const StyledSelect = styled(Select)`
  .ant-select-content-value {
    color: ${(p) => p.theme.colors.secondaryGrey} !important;
  }
  &.ant-select {
    right: -10px;
  }
`;

// The select covers the whole row so all of it is a tap target; the label sits
// on top without catching taps.
const RowWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  height: 44px;
  > :first-child {
    position: relative;
    z-index: 1;
    pointer-events: none;
  }
`;
const RowSelect = styled(Select)`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  &.ant-select {
    padding: 0 !important;
  }
  .ant-select-content {
    text-align: right;
    justify-content: flex-end;
  }
  .ant-select-content-value,
  .ant-select-selection-item {
    color: ${(p) => getNewLookTokens(p.theme).secondary} !important;
    font-size: 13px;
  }
  .ant-select-suffix {
    color: ${(p) => getNewLookTokens(p.theme).muted};
    margin-inline-start: 4px;
  }
`;
